import {
  calculateColumnClass,
  countColumns,
  getColumnClass,
  getFeeColumns,
  getFeeColumnsWithName,
  getMainComponentColumns,
  getMainDepartmentColumns,
  getMainRoleColumns,
  getThirdPartyCostColumns,
  getUserColumns,
  isColumnVisible,
} from '../utils'

import { AuthService } from '@core/service/auth.service'
import { Injectable } from '@angular/core'
import { KeyValue } from '@app/core/types/key-value.type'
import { LanguageService } from '@app/core/service/language.service'
import { Preference } from '@app/core/model/user-preferences.interface'
import { Privilege } from '@app/core/model/enums/privilege.enum'
import { ScopeVersion } from '@app/core/model/scope-version'
import { Subject } from 'rxjs'
import { TableColumnKey } from '../../ui-components/scope-ui-table/table-column-key.enum'
import { FeeItemInstance } from '@app/features/scope-overview/model/fee-item.model'

type ScopeElementState = KeyValue<boolean>

interface ScopeElementStates {
  selected: ScopeElementState
  _selected: Subject<ScopeElementState>
  description: ScopeElementState
}

@Injectable({ providedIn: 'root' })
export class ScopeElementsService {
  deliverableStates: ScopeElementStates = this.scopeElementDefaultState
  deliverableSectionStates: ScopeElementStates = this.scopeElementDefaultState
  componentStates: ScopeElementStates = this.scopeElementDefaultState
  departmentStates: ScopeElementStates = this.scopeElementDefaultState
  deliverableFeeStates: ScopeElementState = {}
  componentFeeStates: ScopeElementState = {}
  sectionFeeStates: ScopeElementState = {}

  userColumns: KeyValue<Preference>
  userColumnsPrefsArray: Preference[]
  timelineColumns: KeyValue<Preference>
  thirdPartyCostColumns: Preference[]
  sectionColumns: Preference[]
  deliverableColumns: Preference[]
  componentColumns: Preference[]
  departmentColumns: Preference[]
  roleColumns: Preference[]

  feeColumns: Preference[]
  scopeFeeColumns: Preference[]
  sectionFeeColumns: Preference[]
  deliverableFeeColumns: Preference[]
  componentFeeColumns: Preference[]
  allFeeColumns: Preference[]
  scopeByRoleFeeColumns: Preference[]

  disableDragging = false
  deliverableDragging = false
  componentDragging = false
  stageDragging = false

  dragTargetId: number

  get scopeElementDefaultState(): ScopeElementStates {
    return { selected: {}, _selected: new Subject<ScopeElementState>(), description: {} }
  }

  constructor(
    private lang: LanguageService,
    private authService: AuthService
  ) {}

  toggleDeliverable(id: number) {
    this.deliverableStates.selected[id] = !this.deliverableStates.selected[id]
    this.deliverableStates._selected.next(this.deliverableStates.selected)
  }

  toggleDeliverableSection(id: number) {
    this.deliverableSectionStates.selected[id] = !this.deliverableSectionStates.selected[id]
    this.deliverableSectionStates._selected.next(this.deliverableSectionStates.selected)
  }

  toggleComponent(id: number) {
    this.componentStates.selected[id] = !this.componentStates.selected[id]
    this.componentStates._selected.next(this.componentStates.selected)
  }

  toggleDepartment(id: number) {
    this.departmentStates.selected[id] = !this.departmentStates.selected[id]
    this.departmentStates._selected.next(this.departmentStates.selected)
  }

  resetTableStates() {
    this.deliverableStates = this.scopeElementDefaultState
    this.deliverableSectionStates = this.scopeElementDefaultState
    this.componentStates = this.scopeElementDefaultState
    this.departmentStates = this.scopeElementDefaultState
    this.deliverableFeeStates = {}
    this.componentFeeStates = {}
    this.sectionFeeStates = {}
  }

  calculateMainColumnClass = () => calculateColumnClass(this.userColumns)
  calculateComponentMainColumnClass = () => calculateColumnClass(this.componentColumns)
  calculateRoleMainColumnClass = () => calculateColumnClass(this.roleColumns)

  calculateScopeTotalsMainColumnClass(currentScope: ScopeVersion) {
    const isSelectedFields = [
      this.userColumns.SCOPEMARK_HOURS,
      this.userColumns.SCOPEMARK_PRICE,
      this.userColumns.AGENCY_HOURS,
      this.userColumns.AGENCY_PRICE,
      this.userColumns.BUDGET,
      this.userColumns.BALANCE,
    ]

    if (currentScope.hasPrivilege(Privilege.SCOPE_MARKUP__VIEW, this.authService.loggedInUser)) {
      isSelectedFields.push(this.userColumns.MARK_UP)
      isSelectedFields.push(this.userColumns.PROFIT)
    }

    return getColumnClass(isSelectedFields.filter((column) => column.selected)?.length)
  }

  setFeeColumns() {
    this.feeColumns = getFeeColumns(this.lang)
    this.scopeFeeColumns = getFeeColumnsWithName(this.feeColumns, `${this.lang.get('scope|u')} FEE`)
    this.sectionFeeColumns = getFeeColumnsWithName(this.feeColumns, `${this.lang.get('stage|u')} FEE`)
    this.deliverableFeeColumns = getFeeColumnsWithName(this.feeColumns, `${this.lang.get('deliverable|u')} FEE`)
    this.componentFeeColumns = getFeeColumnsWithName(this.feeColumns, `${this.lang.get('component|u')} FEE`)
    this.scopeByRoleFeeColumns = getFeeColumnsWithName(this.feeColumns, `FEE NAME`)
    this.allFeeColumns = getFeeColumnsWithName(this.feeColumns, `FEE NAME`)
    this.allFeeColumns.splice(1, 0, {
      key: TableColumnKey.LINK_TO,
      name: 'Link to',
      selected: true,
      isVisibleInList: () => false,
      value: (feeItem: FeeItemInstance) =>
        feeItem.component
          ? { name: feeItem.component.name, id: feeItem.component.id }
          : feeItem.deliverable || feeItem.scopeSection,
      inputType: () => 'linkTo',
    })
  }

  setThirdPartyCostColumns() {
    this.thirdPartyCostColumns = getThirdPartyCostColumns()
  }

  setUserColumns(currentScope: ScopeVersion) {
    this.userColumns = getUserColumns(currentScope, this.authService.loggedInUser, this.lang)

    this.timelineColumns = {
      [TableColumnKey.AGENCY_HOURS]: this.userColumns.AGENCY_HOURS,
      [TableColumnKey.AGENCY_PRICE]: this.userColumns.AGENCY_PRICE,
    }

    this.setUserColumnsArray()
  }

  private setSectionColumns() {
    this.sectionColumns = [
      { key: TableColumnKey.SECTION_NAME, name: 'STAGE', selected: true, isVisibleInList: () => false, field: 'name' },
      ...this.userColumnsPrefsArray,
    ]
  }

  private setDeliverableColumns() {
    this.deliverableColumns = [
      {
        key: TableColumnKey.DELIVERABLE_NAME,
        name: 'DELIVERABLE',
        selected: true,
        isVisibleInList: () => false,
        valueIconFunction: () => 'description',
        field: 'name',
      },
      ...this.userColumnsPrefsArray,
    ]
  }

  private setComponentColumns(currentScope: ScopeVersion) {
    this.componentColumns = [
      ...getMainComponentColumns(currentScope),
      ...this.userColumnsPrefsArray.filter((c) =>
        [
          TableColumnKey.QUANTITY,
          TableColumnKey.START_DATE,
          TableColumnKey.END_DATE,
          TableColumnKey.AGENCY_HOURS,
          TableColumnKey.AGENCY_PRICE,
          TableColumnKey.MARK_UP,
          TableColumnKey.SCOPEMARK_HOURS,
          TableColumnKey.SCOPEMARK_PRICE,
        ].includes(c.key)
      ),
    ]
  }

  private setDepartmentColumns() {
    this.departmentColumns = [
      ...getMainDepartmentColumns(),
      ...this.userColumnsPrefsArray.filter((c) =>
        [
          TableColumnKey.AGENCY_HOURS,
          TableColumnKey.AGENCY_PRICE,
          TableColumnKey.SCOPEMARK_HOURS,
          TableColumnKey.SCOPEMARK_PRICE,
        ].includes(c.key)
      ),
    ]
  }

  private setRoleColumns(currentScope: ScopeVersion) {
    this.roleColumns = [
      ...getMainRoleColumns(currentScope, this.authService.loggedInUser, this.lang),
      ...this.userColumnsPrefsArray.filter((c) =>
        [
          TableColumnKey.AGENCY_HOURS,
          TableColumnKey.AGENCY_PRICE,
          TableColumnKey.SCOPEMARK_HOURS,
          TableColumnKey.SCOPEMARK_PRICE,
        ].includes(c.key)
      ),
    ]
  }

  updateFilterTableColumns(currentScope: ScopeVersion) {
    this.setSectionColumns()
    this.setDeliverableColumns()
    this.setComponentColumns(currentScope)
    this.setDepartmentColumns()
    this.setRoleColumns(currentScope)
    this.unselectExcessColumns()
  }

  setUserColumnsArray() {
    this.userColumnsPrefsArray = Object.values(this.userColumns)
  }

  getSelectedColumnsCount = (): number => countColumns(this.userColumns)

  private unselectExcessColumns() {
    if (this.getSelectedColumnsCount() > 9)
      for (const key of Object.keys(this.userColumns)) {
        if (this.getSelectedColumnsCount() <= 9) return

        const column = this.userColumns[key]

        if (isColumnVisible(column)) column.selected = false
      }
  }
}
